import { useCallback, useEffect, useState } from "react";
import { useNative } from "../../../shared/phone/media";
import { ActionSheet, type SheetAction } from "../../../shared/phone/Sheet";
import { ApiError } from "../../lib/api";
import { listsApi, type ListView, type ListsView } from "../../lib/listsApi";
import { Icon } from "../ui/Icon";
import { AddList, type AddMode } from "./AddList";
import { useCan } from "../../context/AuthContext";
import "../../styles/lists.css";

/**
 * The product's client lists (notes/AUDIENCE.md 5.5, 9): each with what Meta found, when it was added and how
 * many goals use it; Manage adds more people, replaces it, or removes it. Lists belong to the product; a
 * goal uses one only once the owner ticks it on its Audience step.
 */

const day = (sql: string) => new Date(`${sql.replace(" ", "T")}Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const n = (x: number) => x.toLocaleString("en-US");
const MATCHING = "Meta is matching them. This can take a few hours.";

/** How many Meta found: under its floor Meta only says fewer than 1,000. */
export const found = (l: ListView) => (l.belowFloor ? "Meta found fewer than 1,000" : `Meta found about ${n(l.size ?? 0)}`);

/** What a list's line says, by where it is in its life. */
export function listLine(l: ListView): string {
  if (l.state === "failed" || l.state === "draft") return l.note ?? "Meta did not make the list. Try again.";
  if (l.state === "unsure") return "Meta gave an unclear answer. Flashadz looks again in 10 minutes.";
  if (l.state !== "ready") return MATCHING;
  return `${found(l)} · added ${day(l.addedAt)} · ${l.usedBy ? `used by ${l.usedBy} goal${l.usedBy === 1 ? "" : "s"}` : "not used by a goal yet"}`;
}

/** A product's lists and what the owner can do to them, read again after each change. */
function useProductLists(productId: string) {
  const [view, setView] = useState<ListsView | null>(null);
  const [error, setError] = useState<{ id: string; text: string } | null>(null);
  const load = useCallback(() => listsApi.product(productId).then(setView, () => setView({ lists: [], events: [], pixel: false })), [productId]);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: string, f: () => Promise<unknown>) => {
    setError(null);
    try {
      await f();
      await load();
    } catch (e) {
      setError({ id, text: e instanceof ApiError ? e.message : "Something went wrong. Try again." });
    }
  };
  return {
    view,
    error,
    load,
    retry: (l: ListView) => act(l.id, () => listsApi.retry(productId, l.id)),
    remove: (l: ListView) => act(l.id, () => listsApi.remove(productId, l.id)),
  };
}

type Lists = ReturnType<typeof useProductLists>;
interface Props {
  productId: string;
  productName: string;
}

export function ProductLists(props: Props) {
  const lists = useProductLists(props.productId);
  return useNative() ? <ListsNative {...props} lists={lists} /> : <ListsDesktop {...props} lists={lists} />;
}

function ListsDesktop({ productId, productName, lists }: Props & { lists: Lists }) {
  const { view, error, load, retry, remove } = lists;
  const [mode, setMode] = useState<AddMode | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const can = useCan();

  return (
    <section className="card pd-sec">
      <div className="card-h">
        <h3>Client lists</h3>
        {can("editor") && (
          <button className="btn sm" type="button" onClick={() => setMode({ kind: "new" })}>
            <Icon name="plus" />
            Add a list
          </button>
        )}
      </div>
      {view && !view.lists.length && <p className="pd-none">No client lists yet.</p>}
      {view?.lists.map((l) => (
        <div className="listrow" key={l.id}>
          <span className="av">
            <Icon name="users" />
          </span>
          <div>
            <b>
              {l.name}: {n(l.rowsSent)}
            </b>
            <p>{listLine(l)}</p>
            {l.outdated && <p>Outdated: no new people for 30 days.</p>}
            {l.replacing && <p>{l.replacing.state === "failed" ? (l.replacing.note ?? "The new list did not go through.") : "A new list is being matched. It takes this one's place once Meta has matched it."}</p>}
          </div>
          {!can("editor") ? null : l.state === "failed" || l.state === "draft" ? (
            <button className="btn sm" type="button" onClick={() => void retry(l)}>
              Try again
            </button>
          ) : (
            <button className="btn ghost sm" type="button" aria-expanded={open === l.id} onClick={() => setOpen(open === l.id ? null : l.id)}>
              Manage
            </button>
          )}
          {open === l.id && (
            <div className="acts">
              <button className="btn sm" type="button" disabled={l.state !== "ready" && l.state !== "matching"} onClick={() => setMode({ kind: "more", list: l })}>
                Add more people
              </button>
              <button className="btn sm" type="button" disabled={l.state !== "ready" || !!l.replacing} onClick={() => setMode({ kind: "replace", list: l })}>
                Replace
              </button>
              {can("admin") && (
                <button className="btn ghost sm" type="button" onClick={() => void remove(l)}>
                  Remove
                </button>
              )}
            </div>
          )}
          {error?.id === l.id && <p className="warnl acts">{error.text}</p>}
        </div>
      ))}
      <p className="privacy">
        <Icon name="shield" />
        Sent to Meta only to find matches: scrambled first, never kept by Flashadz. Meta deletes what it cannot match.
      </p>
      <AddList productId={productId} productName={productName} mode={mode} onClose={() => setMode(null)} onDone={() => void load()} />
    </section>
  );
}

/** A list's lines on a phone: how many people and what Meta found, then where it stands. */
function nativeLines(l: ListView): { first: string; rest: string[]; waiting: boolean } {
  const people = `${n(l.rowsSent)} ${l.rowsSent === 1 ? "person" : "people"}`;
  const rest = [
    l.state === "ready" ? `Added ${day(l.addedAt)} · ${l.usedBy ? `used by ${l.usedBy} goal${l.usedBy === 1 ? "" : "s"}` : "not used by a goal yet"}` : listLine(l),
  ];
  if (l.outdated) rest.push("Outdated: no new people for 30 days.");
  if (l.replacing) rest.push(l.replacing.state === "failed" ? (l.replacing.note ?? "The new list did not go through.") : "A new list is being matched. It takes this one's place once Meta has matched it.");
  return { first: l.state === "ready" ? `${people} · ${found(l)}` : people, rest, waiting: l.state !== "ready" };
}

/** Client lists on a phone or an iPad: rows, with what can be done to a list in the phone's own menu. */
function ListsNative({ productId, productName, lists }: Props & { lists: Lists }) {
  const { view, error, load, retry, remove } = lists;
  const [mode, setMode] = useState<AddMode | null>(null);
  const [pick, setPick] = useState<ListView | null>(null);
  const [removing, setRemoving] = useState<ListView | null>(null);
  const can = useCan();

  const actions = (l: ListView): SheetAction[] =>
    l.state === "failed" || l.state === "draft"
      ? [{ label: "Try again", onSelect: () => void retry(l) }]
      : [
          { label: "Add more people", disabled: l.state !== "ready" && l.state !== "matching", onSelect: () => setMode({ kind: "more", list: l }) },
          { label: "Replace", disabled: l.state !== "ready" || !!l.replacing, onSelect: () => setMode({ kind: "replace", list: l }) },
          ...(can("admin") ? [{ label: "Remove", danger: true, onSelect: () => setRemoving(l) }] : []),
        ];

  return (
    <section>
      <div className="pn-sh">
        <h2>Client lists</h2>
      </div>
      <div className="pn-grp">
        {can("editor") && (
          <button type="button" className="pn-row accent" onClick={() => setMode({ kind: "new" })}>
            <span className="pn-ico">
              <Icon name="plus" />
            </span>
            <span className="m">
              <span className="t">Add a list</span>
            </span>
          </button>
        )}
        {view?.lists.map((l) => {
          const { first, rest, waiting } = nativeLines(l);
          return (
            <button type="button" className="pn-row wrap pd-lrow" key={l.id} onClick={can("editor") ? () => setPick(l) : undefined}>
              <span className="pn-ico plain">
                <Icon name="users" />
              </span>
              <span className="m">
                <span className="t">{l.name}</span>
                <span className="s">{first}</span>
                {rest.map((t, k) => (
                  <span className={waiting && k === 0 ? "s pd-wait" : "s"} key={k}>
                    {t}
                  </span>
                ))}
                {error?.id === l.id && <span className="s pd-err">{error.text}</span>}
              </span>
              <span className="pd-dots" aria-hidden="true">
                <Icon name="more" />
              </span>
            </button>
          );
        })}
      </div>
      <p className="pn-note pd-priv">
        <Icon name="shield" />
        <span>Sent to Meta only to find matches: scrambled first, never kept by Flashadz. Meta deletes what it cannot match.</span>
      </p>
      <ActionSheet open={!!pick} onClose={() => setPick(null)} title={pick?.name} message={pick ? [nativeLines(pick).first, ...nativeLines(pick).rest].join(". ") : undefined} actions={pick ? actions(pick) : []} />
      <ActionSheet
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.name}?` : undefined}
        message="It is deleted from Meta too."
        actions={removing ? [{ label: "Remove the list", danger: true, onSelect: () => void remove(removing) }] : []}
      />
      <AddList productId={productId} productName={productName} mode={mode} onClose={() => setMode(null)} onDone={() => void load()} />
    </section>
  );
}
